import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { CHART_INK, formatAxisDate, tone, useIsDarkMode, type ThemeTone } from '@/lib/chartTheme';

export interface EngagementSeriesDatum {
  name: string;
  color: ThemeTone;
  data: number[];
  total: number;
}

interface LegendSelectChangedEvent {
  selected: Record<string, boolean>;
}

/**
 * Shared engagement line chart (values over time) — port of CMP's `EChartsLineWebViewContent`
 * (lib-tipmi-frontend-statistics/echart) using echarts-for-react directly. Used by both the user
 * statistics section and the per-post content engagement pane. Legend clicks toggle series
 * visibility natively; the `hiddenSeries` prop lets external controls (e.g. breakdown checkboxes)
 * drive the same state (two-way synced via `onLegendChange`).
 */
export function EngagementChart({
  categories,
  series,
  hiddenSeries,
  onLegendChange,
  height = 280,
  emptyMessage = 'No engagement data for this period.',
}: {
  categories: string[];
  series: EngagementSeriesDatum[];
  hiddenSeries: Record<string, boolean>;
  onLegendChange: (hidden: Record<string, boolean>) => void;
  height?: number;
  emptyMessage?: string;
}) {
  const isDark = useIsDarkMode();

  const option = useMemo(() => {
    const ink = {
      primary: tone(CHART_INK.primary, isDark),
      secondary: tone(CHART_INK.secondary, isDark),
      muted: tone(CHART_INK.muted, isDark),
      grid: tone(CHART_INK.grid, isDark),
      axis: tone(CHART_INK.axis, isDark),
      surface: tone(CHART_INK.surface, isDark),
    };
    const selected = Object.fromEntries(series.map((s) => [s.name, !hiddenSeries[s.name]]));

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: ink.surface,
        borderColor: ink.grid,
        textStyle: { color: ink.primary },
        axisPointer: { type: 'line', lineStyle: { color: ink.axis } },
      },
      legend: {
        data: series.map((s) => s.name),
        selected,
        top: 0,
        itemWidth: 14,
        itemHeight: 8,
        textStyle: { color: ink.secondary },
      },
      grid: { left: 8, right: 16, top: 40, bottom: 8, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: categories.map(formatAxisDate),
        axisLine: { lineStyle: { color: ink.axis } },
        axisTick: { show: false },
        axisLabel: { color: ink.muted },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: ink.muted },
        splitLine: { lineStyle: { color: ink.grid } },
      },
      series: series.map((s) => ({
        name: s.name,
        type: 'line',
        showSymbol: true,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: { width: 2, color: tone(s.color, isDark) },
        itemStyle: { color: tone(s.color, isDark), borderColor: ink.surface, borderWidth: 2 },
        emphasis: { focus: 'series' },
        data: s.data,
      })),
    };
  }, [categories, series, hiddenSeries, isDark]);

  if (series.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <ReactECharts
      option={option}
      notMerge
      style={{ height, width: '100%' }}
      onEvents={{
        legendselectchanged: (params: LegendSelectChangedEvent) => {
          const hidden: Record<string, boolean> = {};
          for (const [name, visible] of Object.entries(params.selected)) {
            if (!visible) hidden[name] = true;
          }
          onLegendChange(hidden);
        },
      }}
    />
  );
}
